import type { FpMessagePart } from "../../../../../../schemas/codegen";
import { isBootsmanResult } from "../shared";

export type SearchResult = {
  title: string;
  description: string;
  url: string;
  content: string;
};

/**
 * Extracts the search query from tool call arguments.
 * Assumes field name is 'query'
 */
function useSearchQuery(part: Extract<FpMessagePart, { type: "tool-call" }>) {
  const args = part.args;
  if (typeof args === "object" && args !== null) {
    const argsObj = args as Record<string, unknown>;
    return (typeof argsObj.query === "string" ? argsObj.query : "") || "";
  }
  return "";
}

/**
 * Parses search results from a completed tool call result.
 * Filters and validates the data to ensure it matches the SearchResult type.
 */
function useSearchResults(part: Extract<FpMessagePart, { type: "tool-call" }>) {
  const isComplete = part.status === "complete";

  if (!isComplete || !part.result) {
    return [];
  }

  if (isBootsmanResult(part.result) && part.result.success) {
    const data = part.result.data;
    if (Array.isArray(data)) {
      return data.filter(
        (item): item is SearchResult =>
          typeof item === "object" &&
          item !== null &&
          typeof item.title === "string" &&
          typeof item.url === "string",
      );
    }
  }

  return [];
}

/**
 * Main hook for extracting web search data from a tool call message part.
 * Provides status flags, search query, and parsed search results.
 */
export function useWebSearchData(
  part: Extract<FpMessagePart, { type: "tool-call" }>,
) {
  const searchQuery = useSearchQuery(part);
  const searchResults = useSearchResults(part);

  return {
    searchQuery,
    searchResults,
  };
}
